import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  messageContentSchema,
  topicContentSchema,
  uuidv7,
  type ContentBlob,
  type Message,
  type Reaction,
  type SpaceMember,
  type DiscussableType,
  type Topic,
  type TopicKind,
  type TopicStatus,
} from "@vmeste/shared";
import { t, useLocale } from "../i18n/index.js";
import { operations } from "../lib/outbox.js";
import {
  nextPinnedOrder,
  pinnedOrderForMove,
  selectDiscussion,
  selectGeneralTopic,
  selectReaction,
  selectReactions,
  selectThreads,
  type SpaceState,
} from "../lib/store.js";
import { syncEngine } from "../lib/sync.js";
import { plaintextOf } from "../lib/plaintext.js";

/**
 * Chat and topics (SPEC §4.5).
 *
 * One entity, two presentations: the space's single `general` topic is a
 * running conversation, and every `thread` is a question with a title and an
 * open/resolved status. Underneath they are the same `message` rows, so one
 * hook serves both and the screen decides which way to draw them.
 *
 * Like lists and wishes, every write goes through the outbox and shows
 * immediately. Nothing here talks to the API.
 */

/*
 * Every write asks the space for its codec (SPEC §3). `null` — an encrypted
 * space this device has no key for — refuses the write instead of falling back
 * to plaintext, which would put readable words into a space whose whole promise
 * is that the server cannot read them.
 */

/**
 * The general topic carries no title anybody typed — `topic.content` is null
 * for it on purpose, so that a server-authored blob never has to exist. The
 * name is the client's to supply.
 *
 * A function rather than a constant: a constant would be read once when this
 * module loads and would keep the language it was loaded in for good.
 */
export function generalTitle(): string {
  return t("chat.general");
}

/** How much of a message is worth showing as the thing a reply answers. */
const EXCERPT = 140;

export interface ReplyView {
  id: string;
  authorName: string;
  excerpt: string;
}

export interface MessageView {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  /** Yours to edit and delete. Nobody else's is (SPEC §4.5). */
  mine: boolean;
  createdAt: string;
  edited: boolean;
  replyTo: ReplyView | null;
  /**
   * The faces standing on this message, one per person, oldest first.
   *
   * Grouped by nobody: with two people in a space there is nothing to group,
   * and a count beside an emoji would be a number that is always one. Who gave
   * it is what a screen shows instead.
   */
  reactions: ReactionView[];
  /** The face *you* gave, if any — what makes a second tap take it back. */
  myReaction: Reaction | null;
}

export interface ReactionView {
  emoji: Reaction;
  userId: string;
  authorName: string;
  mine: boolean;
}

/** What a discussion hangs off. Mirrors `DISCUSSABLE_TYPES` on the wire. */
export interface DiscussionTarget {
  type: DiscussableType;
  id: string;
}

export interface TopicView {
  id: string;
  kind: TopicKind;
  /** What it is about, or null for a thread that stands on its own. */
  target: DiscussionTarget | null;
  title: string;
  status: TopicStatus;
  /** Held at the top of the list, for both of you — pinning is not per device. */
  pinned: boolean;
  updatedAt: string;
  messageCount: number;
  /** The last thing said in it — what a list of threads is actually scanned for. */
  lastMessage: { authorName: string; body: string; at: string } | null;
}

/**
 * A message on its way, answered before it has arrived.
 *
 * The id comes back at once because it is minted here, not by the server —
 * which is what lets a photograph be written to disk against the row it will
 * hang off, minutes or a reload before that row exists. `landed` is the same
 * answer as before, for the caller that has to know whether the thing ever got
 * there.
 */
export interface Sent {
  id: string;
  landed: Promise<boolean>;
}

export interface NewMessage {
  body: string;
  replyToId?: string | null;
}

export interface Chat {
  /** False until the first delta lands, so an empty screen can say which it is. */
  ready: boolean;
  general: TopicView | null;
  threads: TopicView[];
  /** The conversation being read — the general topic unless a thread was opened. */
  topic: TopicView | null;
  messages: MessageView[];
  /**
   * Send, and answer with the message's id once the server has it.
   *
   * `null` when it never got there. The id is the awkward part: a file cannot
   * be attached to a message the server has not seen — it answers "not found",
   * which the outbox is right to treat as permanent — so anything that wants to
   * attach has to wait for this rather than fire alongside it.
   *
   * The message itself still appears instantly; that is the pending overlay's
   * job and it does not wait for any of this.
   */
  /**
   * Put a message in the outbox. `null` only when it could not be composed at
   * all — no space, no topic, a locked codec.
   */
  send(input: NewMessage): Promise<Sent | null>;
  edit(messageId: string, body: string): void;
  remove(messageId: string): void;
  /**
   * Put a face on a message, or take yours back by giving the same one again.
   *
   * Not async and answering nothing: this is the one gesture whose worth is
   * being instant, so it goes through the overlay and the outbox and the screen
   * never waits on it.
   */
  react(messageId: string, emoji: Reaction): void;
  /**
   * Returns the new thread's id, so the screen can open it straight away.
   *
   * `target` anchors it to a wish, an item or an event — the same rows, asked a
   * narrower question. Without one it is an ordinary thread in the chat.
   */
  createThread(title: string, target?: DiscussionTarget): string | null;
  /** The discussion about one thing, if somebody has started it. */
  discussionFor(target: DiscussionTarget): TopicView | null;
  setStatus(topicId: string, status: TopicStatus): void;
  /**
   * Take a thread away, with everything said in it.
   *
   * The general chat is not deletable and the server refuses it; nothing here
   * has to check, but the screen should not offer it either.
   *
   * Worth having because threads are not all typed by hand: discussing a wish,
   * an item or an event makes one, so a thumb landing on «Обсудить» by mistake
   * used to leave a thread that could only ever be marked «решена».
   */
  removeTopic(topicId: string): void;
  /**
   * Hold a thread at the top of the list, or let it back down.
   *
   * Shared, not per device: threads are made by discussing wishes, items and
   * events, so the list grows on its own and the pair needs the same few held
   * still — a pin only one of them could see would be a different list.
   */
  setPinned(topicId: string, pinned: boolean): void;
  /** Move a pinned thread to sit at `toIndex` among the pinned ones. */
  movePinned(topicId: string, toIndex: number): void;
}

/* ------------------------------------------------------------- decoding */

/**
 * Decode a plaintext blob, exactly as `lib/views.ts` does for the list screens.
 *
 * Nothing below this file sees a ContentBlob — that is the codec boundary
 * (SPEC §3), and it is what makes turning on `e2ee` a change to the sync layer
 * alone.
 */
function decodeTitle(blob: ContentBlob | null): string | null {
  const value = plaintextOf(blob);
  if (value === undefined) return null;
  const parsed = topicContentSchema.safeParse(value);
  return parsed.success ? parsed.data.title : null;
}

function decodeBody(blob: ContentBlob): string {
  const value = plaintextOf(blob);
  if (value === undefined) return "";
  const parsed = messageContentSchema.safeParse(value);
  return parsed.success ? parsed.data.body : "";
}

function titleOf(topic: Topic): string {
  if (topic.kind === "general") return generalTitle();
  return decodeTitle(topic.content) ?? t("chat.untitled");
}

function nameOf(members: readonly SpaceMember[], userId: string): string {
  return (
    members.find((member) => member.userId === userId)?.user.displayName ??
    t("chat.someone")
  );
}

/** Exported for its own test: the empty case is the one worth pinning. */
export function excerptOf(body: string): string {
  const flat = body.replace(/\s+/g, " ").trim();
  /*
   * A message with no words is a picture sent without a caption. In the bubble
   * that draws as nothing, which is right — the attachment is underneath it.
   * Here there is no attachment underneath: a thread row and a quoted reply
   * would be a blank line, which reads as something that failed to load.
   */
  if (!flat) return t("chat.wordless");
  return flat.length > EXCERPT ? `${flat.slice(0, EXCERPT)}…` : flat;
}

/* ------------------------------------------------------- local selectors */

/**
 * Messages bucketed by topic, oldest first, in one pass.
 *
 * The screen needs both the open conversation and a one-line summary per
 * thread; asking the store for each topic's messages separately would walk
 * every message once per topic.
 */
function messagesByTopic(state: SpaceState): Map<string, Message[]> {
  const groups = new Map<string, Message[]>();

  for (const message of Object.values(state.messages)) {
    const bucket = groups.get(message.topicId);
    if (bucket) bucket.push(message);
    else groups.set(message.topicId, [message]);
  }

  for (const bucket of groups.values()) {
    bucket.sort((a, b) =>
      a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0,
    );
  }

  return groups;
}

function toTopicView(
  topic: Topic,
  groups: Map<string, Message[]>,
  members: readonly SpaceMember[],
): TopicView {
  const messages = groups.get(topic.id) ?? [];
  const last = messages[messages.length - 1];

  return {
    id: topic.id,
    kind: topic.kind,
    target:
      topic.targetType && topic.targetId
        ? { type: topic.targetType, id: topic.targetId }
        : null,
    title: titleOf(topic),
    status: topic.status,
    pinned: topic.pinnedAt !== null,
    updatedAt: topic.updatedAt,
    messageCount: messages.length,
    lastMessage: last
      ? {
          authorName: nameOf(members, last.authorId),
          body: excerptOf(decodeBody(last.content)),
          at: last.createdAt,
        }
      : null,
  };
}

function toMessageView(
  message: Message,
  state: SpaceState,
  members: readonly SpaceMember[],
  self: string | null,
): MessageView {
  // A reply's target is looked up across the whole space rather than the
  // current bucket: the row it points at may have been deleted, in which case
  // there is simply nothing to quote.
  const target = message.replyToId ? state.messages[message.replyToId] : undefined;

  const reactions = selectReactions(state, message.id)
    .slice()
    .sort((a, b) => (a.updatedAt < b.updatedAt ? -1 : a.updatedAt > b.updatedAt ? 1 : 0))
    .map((row) => ({
      emoji: row.emoji,
      userId: row.userId,
      authorName: nameOf(members, row.userId),
      mine: self !== null && row.userId === self,
    }));

  return {
    id: message.id,
    body: decodeBody(message.content),
    authorId: message.authorId,
    authorName: nameOf(members, message.authorId),
    mine: self !== null && message.authorId === self,
    createdAt: message.createdAt,
    edited: message.editedAt !== null,
    replyTo: target
      ? {
          id: target.id,
          authorName: nameOf(members, target.authorId),
          excerpt: excerptOf(decodeBody(target.content)),
        }
      : null,
    reactions,
    myReaction: reactions.find((row) => row.mine)?.emoji ?? null,
  };
}

const NOTHING: Omit<
  Chat,
  | "send"
  | "edit"
  | "remove"
  | "react"
  | "createThread"
  | "discussionFor"
  | "setStatus"
  | "removeTopic"
  | "setPinned"
  | "movePinned"
> & { activeId: string | null } = {
  ready: false,
  general: null,
  threads: [],
  topic: null,
  messages: [],
  activeId: null,
};

/**
 * @param topicId The thread being read, or null for the general conversation.
 */
export function useChat(
  spaceId: string | null,
  self: string | null,
  topicId: string | null,
): Chat {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);
  // The views built below carry words — «Общий чат», «Без названия», «Кто-то» —
  // so they have to be rebuilt when the language changes, not only when a delta
  // lands.
  const locale = useLocale();

  const read = useMemo(() => {
    if (!spaceId) return NOTHING;

    const state = syncEngine.view(spaceId);
    const groups = messagesByTopic(state);
    const members = state.members;

    const generalRow = selectGeneralTopic(state);

    // A thread that was resolved is still worth reading, so nothing is filtered
    // out here — the screen groups open and resolved instead.
    const threads = selectThreads(state).map((topic) =>
      toTopicView(topic, groups, members),
    );

    const activeRow = topicId ? (state.topics[topicId] ?? null) : generalRow;

    return {
      // `revision` moves off zero on the first delta. Before that an empty
      // conversation and an unloaded one look identical, and saying "тут пусто"
      // to somebody whose history has not arrived yet is a lie.
      ready: state.revision > 0,
      general: generalRow ? toTopicView(generalRow, groups, members) : null,
      threads,
      topic: activeRow ? toTopicView(activeRow, groups, members) : null,
      messages: activeRow
        ? (groups.get(activeRow.id) ?? []).map((message) =>
            toMessageView(message, state, members, self),
          )
        : [],
      activeId: activeRow?.id ?? null,
    };
  }, [spaceId, self, topicId, snapshot, locale]);

  const activeId = read.activeId;

  const send = useCallback(
    async (input: NewMessage): Promise<Sent | null> => {
      if (!spaceId || !activeId) return null;

      /*
       * An empty body is allowed, because a photograph is a message.
       *
       * This refused one, and refused it here — before the outbox, returning
       * `null`, which the composer reads as "the message never landed" and so
       * uploads nothing. A picture sent without a caption therefore vanished in
       * complete silence: no message, no upload, no failed row, nothing to
       * draw. The composer and the content schema had both been opened up; this
       * was the half that stayed shut.
       *
       * What refuses a message that is neither words nor files is the composer,
       * the only place that can see both.
       */
      const body = input.body.trim();

      // Before the awaits below, not between them: a space switched while the
      // encode was out would otherwise decide how this message is written.
      const codec = syncEngine.codec(spaceId);
      if (!codec) return null;

      const replyToId = input.replyToId ?? null;

      /**
       * The notification's text is composed here, next to the message itself.
       *
       * It goes through the same codec, so in an encrypted space the server
       * forwards a blob it cannot read and the service worker decrypts it on
       * the other device (SPEC §3, decision 3). Composing it server-side would
       * mean either plaintext the server can read or a useless "новое
       * сообщение".
       *
       * Kept short on purpose: a Web Push payload caps at 4KB after encryption.
       */
      // Read from the members already synced rather than passed in: the name is
      // what the other person will see on their lock screen.
      const selfName = syncEngine
        .view(spaceId)
        .members.find((member) => member.userId === self)?.user.displayName;

      const summary = {
        title: selfName ? `${selfName}: ${body}` : body,
      };
      const capped = {
        title: summary.title.length > 120 ? `${summary.title.slice(0, 117)}…` : summary.title,
      };

      const [content, notify] = await Promise.all([
        codec.encode({ body }),
        codec.encode(capped),
      ]);

      const operation = operations.createMessage(
        spaceId,
        {
          topicId: activeId,
          content,
          ...(replyToId ? { replyToId } : {}),
        },
        { type: "message.new", targetType: "message", preview: notify },
      );

      syncEngine.mutate(operation);

      /*
       * The id now, the verdict later, and the difference is a photograph.
       *
       * This used to await `whenSettled` and hand back the id only if the
       * message had actually landed. Offline that meant the composer sat on the
       * `File` objects in memory for as long as the outbox held the message —
       * so a reload in between lost the pictures without a word, and the message
       * arrived later with nothing attached to it. The id exists from the moment
       * the operation is minted; giving it out at once is what lets the files be
       * written to disk against it straight away.
       */
      return {
        id: operation.body.id,
        landed: syncEngine.whenSettled(operation.id).then((outcome) => outcome === "sent"),
      };
    },
    [spaceId, activeId, self],
  );

  const edit = useCallback(
    (messageId: string, body: string) => {
      if (!spaceId) return;

      const text = body.trim();
      if (!text) return;

      // Guarded here as well as in the UI: only your own words are yours to
      // change, and a screen is a weak place to enforce that.
      const message = syncEngine.view(spaceId).messages[messageId];
      if (!message || message.authorId !== self) return;

      const codec = syncEngine.codec(spaceId);
      if (!codec) return;

      void codec.encode({ body: text }).then((content) => {
        syncEngine.mutate(operations.updateMessage(spaceId, messageId, { content }));
      });
    },
    [spaceId, self],
  );

  const remove = useCallback(
    (messageId: string) => {
      if (!spaceId) return;

      const message = syncEngine.view(spaceId).messages[messageId];
      if (!message || message.authorId !== self) return;

      syncEngine.mutate(operations.deleteMessage(spaceId, messageId));
    },
    [spaceId, self],
  );

  const react = useCallback(
    (messageId: string, emoji: Reaction) => {
      if (!spaceId || !self) return;

      /*
       * The same face again means "take it back", and reading what is standing
       * has to include the overlay: two taps in a row are two operations queued
       * within a second of each other, and the server's copy still says nothing
       * about the first.
       */
      const standing = selectReaction(syncEngine.view(spaceId), messageId, self);
      const next = standing?.emoji === emoji ? null : emoji;

      syncEngine.mutate(operations.react(spaceId, messageId, { emoji: next }));
    },
    [spaceId, self],
  );

  const createThread = useCallback(
    (title: string, target?: DiscussionTarget): string | null => {
      if (!spaceId) return null;

      const trimmed = title.trim();
      if (!trimmed) return null;

      // Refused before the id is minted: handing back an id for a thread that
      // is never going to be created would open the screen on nothing.
      const codec = syncEngine.codec(spaceId);
      if (!codec) return null;

      // The id is minted here rather than inside the operation so the screen can
      // open the new thread now, without waiting on the encode to resolve.
      const id = uuidv7();

      void codec.encode({ title: trimmed }).then((content) => {
        syncEngine.mutate(
          operations.createTopic(spaceId, {
            id,
            kind: "thread",
            content,
            ...(target ? { targetType: target.type, targetId: target.id } : {}),
          }),
        );
      });

      return id;
    },
    [spaceId],
  );

  const setStatus = useCallback(
    (topic: string, status: TopicStatus) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.updateTopic(spaceId, topic, { status }));
    },
    [spaceId],
  );

  /*
   * Pinning goes out as an ordinary topic update rather than an operation of its
   * own, and that is the point: the outbox already coalesces by row, so pinning
   * and then dragging and then unpinning before the queue drains folds into the
   * one PATCH that describes where the thread actually ended up.
   *
   * No codec anywhere near it. A pin is metadata — a flag and an order key the
   * server sorts by — so it stays outside the blob, and an encrypted space with
   * no key on this device can still be rearranged (SPEC §3, decision 1).
   */
  const removeTopic = useCallback(
    (topic: string) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.deleteTopic(spaceId, topic));
    },
    [spaceId],
  );

  const setPinned = useCallback(
    (topicId: string, pinned: boolean) => {
      if (!spaceId) return;

      if (!pinned) {
        syncEngine.mutate(operations.updateTopic(spaceId, topicId, { pinned: false }));
        return;
      }

      // Last among the pins, computed from what this device can see: a new pin
      // must not push somebody else's arrangement around.
      const pinnedOrder = nextPinnedOrder(syncEngine.view(spaceId));

      syncEngine.mutate(
        operations.updateTopic(spaceId, topicId, { pinned: true, pinnedOrder }),
      );
    },
    [spaceId],
  );

  const movePinned = useCallback(
    (topicId: string, toIndex: number) => {
      if (!spaceId) return;

      const pinnedOrder = pinnedOrderForMove(syncEngine.view(spaceId), topicId, toIndex);
      if (pinnedOrder === null) return;

      syncEngine.mutate(operations.updateTopic(spaceId, topicId, { pinnedOrder }));
    },
    [spaceId],
  );

  /*
   * Built from the same synced state everything else reads, so a discussion
   * started on this device shows up on a wish card the moment it is queued —
   * the pending overlay puts it there before the server has seen it.
   */
  const discussionFor = useCallback(
    (target: DiscussionTarget): TopicView | null => {
      if (!spaceId) return null;

      const state = syncEngine.view(spaceId);
      const topic = selectDiscussion(state, target.type, target.id);
      if (!topic) return null;

      return toTopicView(topic, messagesByTopic(state), state.members);
      // `snapshot` is a dependency because the engine notifies on every change
      // and this reads straight out of the store.
    },
    [spaceId, snapshot],
  );

  return {
    ready: read.ready,
    general: read.general,
    threads: read.threads,
    topic: read.topic,
    messages: read.messages,
    send,
    edit,
    remove,
    react,
    createThread,
    discussionFor,
    setStatus,
    removeTopic,
    setPinned,
    movePinned,
  };
}
